import { useMemo } from 'react';

import config from 'config';
import { useSelector } from 'react-redux';

import { isSavage, isYitty } from '~/shared/utils/brandNameHelper';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useProductContext } from '~/techstyle-shared/react-components';
import { useRegion } from '~/techstyle-shared/react-intl';
import { getProductAge } from '~/techstyle-shared/react-products';
import { getDateNowFunction } from '~/techstyle-shared/redux-core';

import { ProductTag } from '../constants/products';

import { convertStringsToInts } from './convertStringsToInts';
import { getAvailableProductQuantity } from './getAvailableProductQuantity';
import { isCriticalLowStock } from './isCriticalLowStock';
import { isLowStock } from './isLowStock';
import { isPreorderable } from './isPreorderable';
import { isPreorderAvailable } from './isPreorderAvailable';
import { isSoldOut } from './isSoldOut';
import { MEMBERSHIP_BRAND_ID_ENUMS } from './membershipBrandIdEnums';
import useGender from './useGender';

// TODO Add logger's debug

export const Callout = {
  BEST_SELLER: 'BEST_SELLER',
  CRITICAL_STOCK: 'CRITICAL_STOCK',
  SOLD_OUT_KEEP_ON_GRID: 'SOLD_OUT_KEEP_ON_GRID',
  LAST_CHANCE: 'LAST_CHANCE',
  BACK_IN_STOCK: 'BACK_IN_STOCK',
  ECO_STYLE: 'ECO_STYLE',
  SOLD_OUT: 'SOLD_OUT',
  FAKE_SOLD_OUT: 'FAKE_SOLD_OUT',
  MENS_FAKE_SOLD_OUT: 'MENS_FAKE_SOLD_OUT',
  GOLD_ELITE_EXCLUSIVE: 'GOLD_ELITE_EXCLUSIVE',
  KATES_EDIT: 'KATES_EDIT',
  LOW_STOCK: 'LOW_STOCK',
  NEW: 'NEW',
  NEW_TAG: 'NEW_TAG',
  ONLY_X_LEFT: 'ONLY_X_LEFT',
  PLATINUM_EXCLUSIVE: 'PLATINUM_EXCLUSIVE',
  PREORDER: 'PREORDER',
  WAITLIST_AVAILABLE: 'WAITLIST_AVAILABLE',
  EU_EXCLUSIVE: 'EU_EXCLUSIVE',
  DELAYED_SHIPPING: 'DELAYED_SHIPPING',
  RIHANNAS_PICK: 'RIHANNAS_PICK',
  TOP_RATED: 'TOP_RATED',
  TRENDING: 'TRENDING',
  YITTY_COLLECTION: 'YITTY_COLLECTION',
  YITTY: 'YITTY',
  YOUNIVERSAL: 'YOUNIVERSAL',
  KHLOES_PICK: 'KHLOES_PICK',
};

const CalloutTag = {
  [Callout.BACK_IN_STOCK]: ProductTag.backInStock,
  [Callout.BEST_SELLER]: ProductTag.bestSeller,
  [Callout.CUSTOM_TAG]: ProductTag.customTag,
  [Callout.NEW_TAG]: ProductTag.new,
  [Callout.RIHANNAS_PICK]: ProductTag.rihannasPick,
  [Callout.TOP_RATED]: ProductTag.topRated,
  [Callout.TRENDING]: ProductTag.trending,
  [Callout.YOUNIVERSAL]: ProductTag.youniversal,
  [Callout.ECO_STYLE]: ProductTag.ecoStyle,
  [Callout.PREORDER]: ProductTag.preorder,
  [Callout.KHLOES_PICK]: ProductTag.khloesPick,
  [Callout.LAST_CHANCE]: ProductTag.lastChance,
  [Callout.SOLD_OUT_KEEP_ON_GRID]: ProductTag.stickySoldOut,
  [Callout.KATES_EDIT]: ProductTag.katesEdit,
  [Callout.DELAYED_SHIPPING]: ProductTag.delayedShipping,
  [Callout.PLATINUM_EXCLUSIVE]: ProductTag.platinumExclusive,
  [Callout.EU_EXCLUSIVE]: ProductTag.euExclusive,
};

const getCalloutPriorityList = () => {
  if (isSavage()) {
    return [
      Callout.PREORDER,
      Callout.CRITICAL_STOCK,
      Callout.LOW_STOCK,
      Callout.YOUNIVERSAL,
      Callout.RIHANNAS_PICK,
      Callout.BACK_IN_STOCK,
      Callout.BEST_SELLER,
      Callout.TOP_RATED,
      Callout.NEW_TAG,
      Callout.TRENDING,
      Callout.CUSTOM_TAG,
    ];
  }

  return [
    Callout.SOLD_OUT_KEEP_ON_GRID,
    Callout.LAST_CHANCE,
    Callout.DELAYED_SHIPPING,
    Callout.PREORDER,
    Callout.EU_EXCLUSIVE,
    Callout.SOLD_OUT,
    Callout.FAKE_SOLD_OUT,
    Callout.MENS_FAKE_SOLD_OUT,
    Callout.KHLOES_PICK,
    Callout.PLATINUM_EXCLUSIVE,
    Callout.KATES_EDIT,
    Callout.ECO_STYLE,
    Callout.BACK_IN_STOCK,
    Callout.WAITLIST_AVAILABLE,
    Callout.LOW_STOCK,
    Callout.ONLY_X_LEFT,
    Callout.GOLD_ELITE_EXCLUSIVE,
    Callout.NEW,
    Callout.YITTY,
    Callout.YITTY_COLLECTION,
  ];
};

export default function useProductCallout({
  product: productFromProps,
  callouts = getCalloutPriorityList(),
  isGridCallout = false,
} = {}) {
  const region = useRegion();
  const productFromContext = useProductContext();
  const product = productFromProps || productFromContext;
  const currentDate = useSelector(getDateNowFunction)();
  const { isVip, isLead } = useMembership();
  const { isMale } = useGender();
  const tagIdSet = useMemo(
    () => new Set(convertStringsToInts(product.tagIdList)),
    [product.tagIdList]
  );
  const availableProductQuantity = getAvailableProductQuantity(product);
  const membershipBrandId = product.membershipBrandId;

  for (const type of callouts) {
    switch (type) {
      case Callout.YITTY: {
        if (isYitty()) {
          break;
        }
        if (
          isGridCallout &&
          membershipBrandId === MEMBERSHIP_BRAND_ID_ENUMS.YITTY
        ) {
          return { type };
        }
        break;
      }
      case Callout.YITTY_COLLECTION: {
        if (membershipBrandId === MEMBERSHIP_BRAND_ID_ENUMS.YITTY) {
          return { type };
        }
        break;
      }

      case Callout.PREORDER: {
        if (isPreorderAvailable(product) && isPreorderable(product)) {
          return { type };
        }
        break;
      }

      case Callout.SOLD_OUT: {
        if (isSoldOut(product)) {
          return { type };
        }
        break;
      }

      case Callout.FAKE_SOLD_OUT: {
        if (isVip && tagIdSet.has(ProductTag.fakeSoldOut)) {
          return { type };
        }
        break;
      }

      case Callout.MENS_FAKE_SOLD_OUT: {
        const mensLeadLimitedAvailability =
          isMale && isLead && tagIdSet.has(ProductTag.leadLimitedAvailability);

        // This tag shows fake sold out on men's products for women profiles
        const mensFakeSoldOut =
          !isMale && tagIdSet.has(ProductTag.fakeSoldOutMens);
        if (mensLeadLimitedAvailability || mensFakeSoldOut) {
          return { type };
        }
        break;
      }
      case Callout.WAITLIST_AVAILABLE: {
        // From https://git.justfab.net/projects/ECOM/repos/fabletics/browse/sites/secure.fabletics.com/fusebox.init.inc.cfm#693-699
        // And https://git.justfab.net/projects/ECOM/repos/fabletics/browse/sites/secure.fabletics.com/components/Enterprise/Beans/Product/FableticsProductGridBean.cfc#561-563
        if (isVip && product.waitListAllowed && availableProductQuantity <= 0) {
          return { type };
        }
        break;
      }
      case Callout.LOW_STOCK: {
        const locationLowStockIndicator = config.get(
          'public.quantityRules.locationLowStockIndicator'
        );
        const lowStockRules = locationLowStockIndicator.masterProduct[region];
        const thresholdOverride = !isGridCallout
          ? lowStockRules.lowSizeStockMax
          : null;
        // From https://git.justfab.net/projects/ECOM/repos/fabletics/browse/sites/secure.fabletics.com/components/Enterprise/Beans/Product/FableticsProductGridBean.cfc#565-567
        if (isLowStock({ product, region, thresholdOverride })) {
          return { type };
        }
        break;
      }
      case Callout.CRITICAL_STOCK: {
        // From https://git.justfab.net/projects/ECOM/repos/fabletics/browse/sites/secure.fabletics.com/components/Enterprise/Beans/Product/FableticsProductGridBean.cfc#565-567
        if (isCriticalLowStock({ product, region })) {
          return { type };
        }
        break;
      }
      case Callout.ONLY_X_LEFT: {
        if (isCriticalLowStock({ product, region })) {
          return { type, value: availableProductQuantity };
        }
        break;
      }
      case Callout.GOLD_ELITE_EXCLUSIVE: {
        // Callout only, no purchasing logic implemented
        if (isVip && tagIdSet.has(ProductTag.goldEliteExclusive)) {
          return { type };
        }
        break;
      }
      case Callout.NEW: {
        try {
          const { days: ageDays } = getProductAge({ currentDate, product });
          if (ageDays <= 32) {
            return { type };
          }
        } catch (error) {
          // nothing is needed to be returned here if we error out
        }
        break;
      }
      case Callout.EU_EXCLUSIVE:
      case Callout.PLATINUM_EXCLUSIVE:
      case Callout.DELAYED_SHIPPING:
      case Callout.KATES_EDIT:
      case Callout.ECO_STYLE:
      case Callout.SOLD_OUT_KEEP_ON_GRID:
      case Callout.LAST_CHANCE:
      case Callout.KHLOES_PICK:
      case Callout.NEW_TAG:
      case Callout.PRE_ORDER:
      case Callout.RIHANNAS_PICK:
      case Callout.BACK_IN_STOCK:
      case Callout.BEST_SELLER:
      case Callout.TOP_RATED:
      case Callout.YOUNIVERSAL:
      case Callout.TRENDING: {
        if (tagIdSet.has(CalloutTag[type])) {
          return { type };
        }
        break;
      }
      // Additional callouts go here
      default:
        // debug('No Callout found for %s, type)
        break;
    }
  }
  // No callouts found
  return null;
}
